/**
 * Admin contracts for the catalog an organization administers: groups, the projects and shared
 * activities assigned to them, and the audit trail of those changes (design §5.8, §5.9, §5.10).
 */
import { z } from 'zod';
import { ACTIVITY_TYPES, ENTITY_STATUSES, USER_ROLES } from '../enums.js';
import { instantSchema, isoDateSchema, paginated, paginationQuerySchema, sortQuerySchema, uuidSchema, } from './common.js';
import { hexColorSchema } from './admin.js';
const name = (max = 200) => z.string().trim().min(1).max(max);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
// ------------------------------------------------------------------ groups
export const groupAdminSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    memberCount: z.number().int().nonnegative(),
    projectCount: z.number().int().nonnegative(),
    activityCount: z.number().int().nonnegative(),
});
export const groupDetailSchema = groupAdminSchema.extend({
    members: z.array(z.object({
        id: uuidSchema,
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        role: z.enum(USER_ROLES),
    })),
    projects: z.array(z.object({ id: uuidSchema, name: z.string(), color: z.string() })),
    activities: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export const GROUP_SORT_FIELDS = ['name', 'memberCount'];
export const listGroupsQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
    sort: sortQuerySchema(GROUP_SORT_FIELDS),
});
export const groupListSchema = paginated(groupAdminSchema);
export const createGroupSchema = z.object({
    name: name(),
    description: optionalText(2000),
});
export const updateGroupSchema = createGroupSchema.partial();
export const idSetSchema = z.object({ ids: z.array(uuidSchema).max(500) });
// ---------------------------------------------------------------- projects
export const createProjectSchema = z.object({
    name: name(),
    code: optionalText(50),
    clientName: optionalText(200),
    description: optionalText(2000),
    color: hexColorSchema,
    groupIds: z.array(uuidSchema).max(200).default([]),
});
/** Colour alone is still a valid body, which is all the first slice of the screen sent. */
export const updateProjectSchema = z
    .object({
    name: name(),
    code: optionalText(50),
    clientName: optionalText(200),
    description: optionalText(2000),
    color: hexColorSchema,
})
    .partial();
// -------------------------------------------------------------- activities
export const activityAdminSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    type: z.enum(ACTIVITY_TYPES),
    status: z.enum(ENTITY_STATUSES),
    /**
     * The project this activity belongs to. Null means it belongs to none and can be booked to
     * nowhere — what the 0005 split left behind for activities with no history to map. The admin
     * screen lists those separately so they can be placed or archived.
     */
    project: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    owner: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    groupCount: z.number().int().nonnegative(),
    entryCount: z.number().int().nonnegative(),
    mergedIntoActivityId: uuidSchema.nullable(),
});
export const ACTIVITY_SORT_FIELDS = ['name', 'project', 'type', 'status', 'entryCount'];
export const listActivitiesQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(100).optional(),
    status: z.enum(ENTITY_STATUSES).optional(),
    type: z.enum(ACTIVITY_TYPES).optional(),
    ownerUserId: uuidSchema.optional(),
    groupId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    /** Only the activities belonging to no project — the ones nobody can book to. */
    unassigned: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((v) => v === true || v === 'true'),
    sort: sortQuerySchema(ACTIVITY_SORT_FIELDS),
});
export const activityListSchema = paginated(activityAdminSchema);
export const createActivitySchema = z.object({
    /** An activity belongs to one project, so creating one means saying which. */
    projectId: uuidSchema,
    name: name(),
    description: optionalText(2000),
    groupIds: z.array(uuidSchema).max(200).default([]),
});
export const updateActivitySchema = z
    .object({
    name: name(),
    description: optionalText(2000),
    /**
     * Moving an activity to another project is only allowed while it has no time entries: the
     * entries would otherwise be left pointing at an activity of a project they do not belong to,
     * which is the one invariant the 0005 split exists to establish. This is how the activities
     * that split left unassigned get placed.
     */
    projectId: uuidSchema,
})
    .partial();
export const promoteActivitySchema = z.object({
    groupIds: z.array(uuidSchema).max(200).default([]),
});
export const mergeActivitySchema = z.object({ targetActivityId: uuidSchema });
export const mergeResultSchema = z.object({
    movedEntries: z.number().int().nonnegative(),
    target: activityAdminSchema,
});
// -------------------------------------------------------------- audit log
export const auditLogSchema = z.object({
    id: uuidSchema,
    action: z.string(),
    entityType: z.string(),
    entityId: uuidSchema.nullable(),
    actor: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    targetUser: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
    ip: z.string().nullable(),
    requestId: z.string().nullable(),
    createdAt: instantSchema,
});
export const listAuditLogsQuerySchema = paginationQuerySchema.extend({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    actorUserId: uuidSchema.optional(),
    entityType: z.string().trim().max(50).optional(),
    entityId: uuidSchema.optional(),
    action: z.string().trim().max(100).optional(),
});
export const auditLogListSchema = paginated(auditLogSchema);
//# sourceMappingURL=admin-catalog.js.map